import { useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import {
  getFinanceGaSummary,
  getFinanceGaTimeseries,
  type GaSummary,
  type GaCountryMtd,
  type GaTimeseriesResult,
  type GaTimeseriesPeriod,
  type GaGranularity,
} from '../api/rondo';
import { useTabParam } from '../shell/useTabParam';
import { ServiceBar } from '../shell/ServiceBar';
import { ArcTabs } from '../components/arc/primitives/arc-primitives';
import { Card } from '../components/arc/card/card';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { PageState } from '../components/arc/page-state/page-state';
import { useRondoPageData } from './useRondoPageData';
import { SortableDataTable, type DataColumn } from '../components/arc/sortable-data-table/sortable-data-table';
import './finance-ga.css';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);
const nfGa = new Intl.NumberFormat('cs-CZ');

type SubTab = 'zakladni' | 'prehled' | 'vypis';
const TRZBY_TABS: readonly SubTab[] = ['zakladni', 'prehled', 'vypis'];
type Country = 'ALL' | 'CZ' | 'SK' | 'RO';

const COUNTRY_META: Record<Country, { flag: string; name: string; host: string }> = {
  ALL: { flag: '🌐', name: 'Celkem (CZ+SK+RO)', host: '' },
  CZ: { flag: '🇨🇿', name: 'Česko', host: 'rondo.cz' },
  SK: { flag: '🇸🇰', name: 'Slovensko', host: 'rondogo.sk' },
  RO: { flag: '🇷🇴', name: 'Rumunsko', host: 'rondogo.ro' },
};

const fmtKc = (n: number) => `${n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč`;
const fmtN = (n: number) => Math.round(n).toLocaleString('cs-CZ');
const fmtEcpm = (rev: number, imp: number) => (imp > 0 ? fmtKc((rev / imp) * 1000) : '-');

function fmtDelta(curr: number, prev: number): ReactNode {
  if (!prev) return <span className="text-muted">-</span>;
  const pct = ((curr - prev) / prev) * 100;
  const up = pct >= 0;
  const cls = up ? 'text-success' : 'text-danger';
  return <span className={cls}>{up ? '▲' : '▼'} {up ? '+' : ''}{pct.toFixed(1)}%</span>;
}

export function FinanceTrzbyPage() {
  const [tab, setTab] = useTabParam<SubTab>('tab', TRZBY_TABS, 'zakladni');

  return (
    <section className="rondo-ga-revenue rondo-arc-ga p1-arc-scope">
      <header className="prototype-page-head"><div><div className="prototype-eyebrow rondo">FINANCE</div><h1>GA Odhad tržeb</h1><p>Odhad reklamních výnosů podle návštěvnosti Google Analytics</p></div></header>
      <ArcTabs label="GA odhad tržeb" items={[{ value: 'zakladni', label: 'Základní' }, { value: 'prehled', label: 'Přehled' }, { value: 'vypis', label: 'Výpis' }]} value={tab} onChange={setTab} className="rondo-ga-arc-tabs" />

      {tab === 'zakladni' ? <ZakladniTab /> : tab === 'prehled' ? <PrehledChartTab /> : <TimeseriesTab />}
    </section>
  );
}
function ZakladniTab() {
  const { data, error, loading } = useRondoPageData<GaSummary>('finance-ga-summary', getFinanceGaSummary);

  if (loading) return <PageState title="Načítám odhad tržeb" loading />;
  if (error) return <PageState title="Odhad tržeb se nepodařilo načíst" message={error instanceof ApiError ? error.message : 'Načtení selhalo'} />;
  if (!data) return null;

  const period = new Date(data.mtdPeriod).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });

  return (
    <>
      <p className="rondo-arc-ga-period">Výnosy od začátku měsíce · {period}</p>
      <div className="rondo-arc-ga-market-grid">
        {(['ALL', 'CZ', 'SK', 'RO'] as Country[]).map((c) => (
          <MtdBox key={c} country={c} m={data.countries[c]} />
        ))}
      </div>
    </>
  );
}

function MtdBox({ country, m }: { country: Country; m: GaCountryMtd }) {
  const meta = COUNTRY_META[country];
  const isAll = country === 'ALL';
  return (
    <Card title={`${meta.flag} ${meta.name}`} description={meta.host || 'Všechny trhy'} className={`rondo-arc-ga-market-card${isAll ? ' total' : ''}`}>
      <div className="rondo-arc-ga-revenue"><small>Odhad tržeb</small><strong>{fmtKc(m.revenue)}</strong><p>Proti minulému měsíci {fmtDelta(m.revenue, m.prevRevenue)} <span>{fmtKc(m.prevRevenue)}</span></p></div>
      <div className="rondo-arc-ga-market-meta"><div><small>Imprese</small><strong>{fmtN(m.impressions)}</strong></div><div><small>eCPM</small><strong>{fmtEcpm(m.revenue, m.impressions)}</strong></div></div>
    </Card>
  );
}

type ChartRange = '1d' | '7d' | '30d' | '90d' | 'mtd';
const RANGE_LABELS: Record<ChartRange, string> = {
  '1d': 'Dnes', '7d': 'Posledních 7 dní', '30d': 'Posledních 30 dní',
  '90d': 'Posledních 90 dní', mtd: 'Aktuální měsíc (MTD)',
};
const COUNTRY_LABELS: Record<Country, string> = {
  ALL: 'Celkem', CZ: 'Česko', SK: 'Slovensko', RO: 'Rumunsko',
};

function PrehledChartTab() {
  const [country, setCountry] = useState<Country>('ALL');
  const [range, setRange] = useState<ChartRange>('7d');
  const [data, setData] = useState<GaTimeseriesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFinanceGaTimeseries({ country, granularity: 'day' })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [country]);

  const periods = (() => {
    const all = data?.periods ?? [];
    if (range === 'mtd') {
      const now = new Date();
      const prefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      return all.filter((p) => p.key.startsWith(prefix));
    }
    const cnt = range === '1d' ? 1 : range === '7d' ? 7 : range === '30d' ? 30 : 90;
    return all.slice(-cnt);
  })();

  const kpi = periods.reduce(
    (a, p) => ({ rev: a.rev + p.revenue, imp: a.imp + p.impressions, clk: a.clk + p.clicks }),
    { rev: 0, imp: 0, clk: 0 },
  );

  return (
    <>
      <ServiceBar variant="seg" selects={[
        { label: 'Země', value: country, onChange: (v) => setCountry(v as Country), options: [{ value: 'ALL', label: 'Všechny' }, { value: 'CZ', label: '🇨🇿 CZ' }, { value: 'SK', label: '🇸🇰 SK' }, { value: 'RO', label: '🇷🇴 RO' }] },
        { label: 'Rozsah', value: range, onChange: (v) => setRange(v as ChartRange), options: [{ value: '1d', label: 'Den' }, { value: '7d', label: '7 dní' }, { value: '30d', label: '30 dní' }, { value: '90d', label: '90 dní' }, { value: 'mtd', label: 'Měsíc kalend.' }] },
      ]} onReset={() => { setCountry('ALL'); setRange('7d'); }} right={<div className="ga-filter-meta">{loading ? 'Načítám…' : `${COUNTRY_LABELS[country]} · ${RANGE_LABELS[range]}`}</div>} />

      <div className="rondo-arc-ga-kpis">
        <MetricCard label="Tržby" value={kpi.rev} suffix={'\u00a0Kč'} decimals={2} context={RANGE_LABELS[range]} />
        <MetricCard label="Imprese" value={kpi.imp} context={COUNTRY_LABELS[country]} />
        <MetricCard label="Kliky" value={kpi.clk} context={COUNTRY_LABELS[country]} />
        <MetricCard label="eCPM" value={kpi.imp > 0 ? (kpi.rev / kpi.imp) * 1000 : 0} suffix={'\u00a0Kč'} decimals={2} context="Za 1 000 impresí" />
      </div>

      {error ? <PageState title="Graf se nepodařilo načíst" message={error} /> :
        <Card title="Odhad tržeb a návštěvnost" description={RANGE_LABELS[range]} className="rondo-arc-ga-chart-card">
          {loading ? <PageState title="Načítám vývoj v čase" loading /> : periods.length === 0 ? <p>Žádná data.</p> : <MultiLineChart periods={periods} />}
        </Card>}
    </>
  );
}

function MultiLineChart({ periods }: { periods: GaTimeseriesPeriod[] }) {
  const data = {
    labels: periods.map((p) => p.label.slice(0, 5)),
    datasets: [
      { label: 'Tržby (Kč)', data: periods.map((p) => p.revenue), borderColor: '#d86c18', backgroundColor: 'rgba(216,108,24,0.08)', borderWidth: 2.5, fill: true, tension: 0.28, pointRadius: 2, pointHoverRadius: 5, yAxisID: 'y' },
      { label: 'Imprese', data: periods.map((p) => p.impressions), borderColor: '#16804a', backgroundColor: 'transparent', borderWidth: 2, fill: false, tension: 0.28, pointRadius: 2, pointHoverRadius: 5, yAxisID: 'y2' },
      { label: 'Kliky', data: periods.map((p) => p.clicks), borderColor: '#6d55b3', backgroundColor: 'transparent', borderWidth: 2, fill: false, tension: 0.28, pointRadius: 2, pointHoverRadius: 5, yAxisID: 'y2' },
    ],
  };
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true, position: 'bottom', labels: { font: { size: 11 }, usePointStyle: true, pointStyle: 'line' } },
      tooltip: {
        callbacks: {
          label: (ctx: any) =>
            `${ctx.dataset.label}: ${ctx.dataset.yAxisID === 'y' ? nfGa.format(ctx.parsed.y) + ' Kč' : nfGa.format(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: { ticks: { font: { size: 11 }, maxTicksLimit: 12 } },
      y: { position: 'left', ticks: { font: { size: 11 }, color: '#d86c18', callback: (v: any) => nfGa.format(v) + ' Kč' }, title: { display: true, text: 'Tržby (Kč)', font: { size: 10 }, color: '#d86c18' } },
      y2: { position: 'right', grid: { drawOnChartArea: false }, ticks: { font: { size: 11 }, color: '#16804a', callback: (v: any) => nfGa.format(v) }, title: { display: true, text: 'Imprese / Kliky', font: { size: 10 }, color: '#16804a' } },
    },
  };
  return (
    <div style={{ height: 380, position: 'relative' }}>
      <Line data={data} options={options} />
    </div>
  );
}

function TimeseriesTab() {
  const [country, setCountry] = useState<Country>('ALL');
  const [granularity, setGranularity] = useState<GaGranularity>('day');
  const [data, setData] = useState<GaTimeseriesResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFinanceGaTimeseries({ country, granularity })
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [country, granularity]);

  const periods = data?.periods ?? [];
  const rows = [...periods].reverse().map((period) => ({ ...period }));
  type TableRow = (typeof rows)[number];
  const columns: DataColumn<TableRow>[] = [
    { key: 'key', label: 'Období', width: 170, render: (_, row) => row.label },
    { key: 'revenue', label: 'Tržby', numeric: true, width: 170, render: (_, row) => fmtKc(row.revenue) },
    { key: 'impressions', label: 'Imprese', numeric: true, width: 150, render: (_, row) => fmtN(row.impressions) },
    { key: 'clicks', label: 'Kliky', numeric: true, width: 130, render: (_, row) => fmtN(row.clicks) },
    { key: 'ecpm', label: 'eCPM', numeric: true, width: 150, render: (_, row) => row.impressions > 0 ? fmtKc(row.ecpm) : '—' },
  ];

  return (
    <>
      <ServiceBar variant="seg" selects={[
        { label: 'Země', value: country, onChange: (v) => setCountry(v as Country), options: [{ value: 'ALL', label: 'Všechny' }, { value: 'CZ', label: '🇨🇿 CZ' }, { value: 'SK', label: '🇸🇰 SK' }, { value: 'RO', label: '🇷🇴 RO' }] },
        { label: 'Granularita', value: granularity, onChange: (v) => setGranularity(v as GaGranularity), options: [{ value: 'day', label: 'Den' }, { value: 'week', label: 'Týden' }, { value: 'month', label: 'Měsíc' }] },
      ]} onReset={() => { setCountry('ALL'); setGranularity('day'); }} right={<div className="ga-filter-meta">{loading ? 'Načítám…' : `${periods.length} období`}</div>} />

      <div className="rondo-arc-ga-table">
        {error && <PageState title="Výpis se nepodařilo načíst" message={error} />}
        {loading ? <PageState title="Načítám výpis" loading /> : !error && <SortableDataTable rows={rows} columns={columns} rowKey="key" caption="GA odhad tržeb" emptyMessage="Žádná data." />}
      </div>
    </>
  );
}
